import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";

const PLANS = [
  { key: "premium", name: "Premium", audience: "For High-Achiever Students", monthly: 20, yearly: 9.93, features: ["Unlimited monthly chats", "100 PDF uploads", "50 MB per PDF", "Multi-doc chat", "Generate reports & essays", "Priority support"] },
  { key: "professional", name: "Professional", audience: "For Ambitious Professionals", monthly: 30, yearly: 14.93, highlight: true, features: ["Unlimited monthly chats", "1,000 PDF uploads", "100 MB per PDF", "Full API access", "Share your chatbot", "Embed on your website", "Priority access to new features"] },
  { key: "enterprise", name: "Enterprise", audience: "For Top-Performer Businesses", monthly: 80, yearly: 39.93, features: ["Unlimited monthly chats", "10,000 PDF uploads", "150 MB per PDF", "Remove DocuMind branding", "Dedicated 24/7 support", "Priority feature requests"] },
];

export const startCheckout = async (lookupKey) => {
  if (lookupKey.startsWith("inr:")) {
    return startRazorpay(lookupKey.slice(4), () => { window.location.href = "/app"; });
  }
  if (lookupKey.startsWith("pp:")) {
    return startPayPal(lookupKey.slice(3));
  }
  const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
  if (data.switched) return data;
  window.location.href = data.checkout_url;
  return data;
};

export const startPayPal = async (lookupKey) => {
  const { data } = await api.post("/payments/paypal/order", { lookup_key: lookupKey, origin_url: window.location.origin });
  window.location.href = data.approve_url;
  return data;
};

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve;
  s.onerror = () => reject(new Error("Couldn't load the rupee checkout"));
  document.body.appendChild(s);
});

export const startRazorpay = async (lookupKey, onPaid, onClose) => {
  const { data } = await api.post("/payments/razorpay/order", { lookup_key: lookupKey });
  await loadRazorpay();
  const [plan, interval] = lookupKey.split("_");
  const rzp = new window.Razorpay({
    key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
    name: "DocuMind", description: `${plan[0].toUpperCase() + plan.slice(1)} plan · ${interval}`,
    prefill: { name: data.name, email: data.email, contact: data.phone || "" },
    theme: { color: "#002fa7" },
    handler: async (resp) => {
      try {
        await api.post("/payments/razorpay/verify", {
          order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature,
        });
        toast.success("Payment received. Your plan is active.");
        onPaid?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "We couldn't confirm the payment");
        onClose?.();
      }
    },
    modal: { ondismiss: () => onClose?.() },
  });
  rzp.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed"); });
  rzp.open();
  return data;
};

export const resumePendingPlan = async () => {
  const pending = sessionStorage.getItem("documind_pending_plan");
  if (!pending) return false;
  sessionStorage.removeItem("documind_pending_plan");
  try {
    await startCheckout(pending);
  } catch (e) {
    toast.error(e.response?.data?.detail || "Your account is ready, but checkout couldn't start. Pick a plan again.");
    return false;
  }
  return true;
};

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const cfg = useConfig();
  const rzpOn = !!cfg?.razorpay?.enabled;
  const ppOn = !!cfg?.paypal?.enabled;
  const [interval, setInterval] = useState("yearly");
  const [currency, setCurrency] = useState("usd");
  const [busy, setBusy] = useState(null);
  const inr = currency === "inr" && rzpOn;
  const paypal = currency === "paypal" && ppOn;
  const prepaid = inr || paypal;
  const inrPrice = (key) => cfg?.razorpay?.prices_inr?.[`${key}_${interval}`];
  const ppPrice = (key) => cfg?.paypal?.prices_usd?.[`${key}_${interval}`];
  const payOptions = [["usd", "USD · card"], ...(ppOn ? [["paypal", "USD · PayPal"]] : []), ...(rzpOn ? [["inr", "INR · UPI & cards"]] : [])];

  const choose = async (plan) => {
    const lookup = `${plan.key}_${interval}`;
    if (!user) {
      sessionStorage.setItem("documind_pending_plan", inr ? `inr:${lookup}` : paypal ? `pp:${lookup}` : lookup);
      return nav("/signup");
    }
    setBusy(plan.key);
    if (paypal) {
      try {
        await startPayPal(lookup);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Could not start PayPal checkout");
        setBusy(null);
      }
      return;
    }
    if (inr) {
      try {
        await startRazorpay(lookup, async () => { await refresh(); setBusy(null); }, () => setBusy(null));
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Could not start checkout");
        setBusy(null);
      }
      return;
    }
    try {
      const res = await startCheckout(lookup);
      if (res?.switched) {
        await refresh();
        toast.success(`Switched to ${plan.name} (${interval}). Stripe prorates the difference.`);
        setBusy(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <section id="pricing" className="bg-muted border-y border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="font-mono-label text-muted-foreground mb-3">Pricing</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter max-w-2xl mb-6">Stop working so hard. Put AI to work.</h2>
        <div className="flex flex-wrap gap-4 mb-16">
        <div className="inline-flex border border-foreground" data-testid="pricing-interval-toggle">
          {["monthly", "yearly"].map((i) => (
            <button key={i} data-testid={`pricing-interval-${i}`} onClick={() => setInterval(i)}
              className={`font-mono-label px-5 py-2.5 ${interval === i ? "bg-foreground text-background" : "hover:bg-background"}`}>
              {i}{i === "yearly" && " · save 30%+"}
            </button>
          ))}
        </div>
        {payOptions.length > 1 && (
          <div className="inline-flex border border-foreground" data-testid="pricing-currency-toggle">
            {payOptions.map(([k, l]) => (
              <button key={k} data-testid={`pricing-currency-${k}`} onClick={() => setCurrency(k)}
                className={`font-mono-label px-5 py-2.5 ${currency === k ? "bg-foreground text-background" : "hover:bg-background"}`}>{l}</button>
            ))}
          </div>
        )}
        </div>
        <div className="grid md:grid-cols-3 border-t border-l border-border bg-background">
          {PLANS.map((p) => {
            const current = user?.plan === p.key && user?.plan_interval === interval;
            const paid = user?.plan && user.plan !== "free";
            return (
              <div key={p.key} className={`grid-cell p-10 relative ${p.highlight ? "border-primary border-2 -m-px" : ""}`}>
                {p.highlight && <div className="absolute -top-3 left-10 bg-primary text-primary-foreground font-mono-label px-3 py-1">Most popular</div>}
                <div className="font-serif-display text-3xl mb-1">{p.name}</div>
                <div className="text-sm text-muted-foreground mb-6">{p.audience}</div>
                <div className="mb-1" data-testid={`pricing-price-${p.key}`}>
                  {inr ? (
                    <>
                      <span className="font-serif-display text-5xl">₹{(inrPrice(p.key) || 0).toLocaleString("en-IN")}</span>
                      <span className="text-muted-foreground"> / {interval === "yearly" ? "year" : "month"}</span>
                    </>
                  ) : paypal ? (
                    <>
                      <span className="font-serif-display text-5xl">${(ppPrice(p.key) || 0).toFixed(2)}</span>
                      <span className="text-muted-foreground"> / {interval === "yearly" ? "year" : "month"}</span>
                    </>
                  ) : (
                    <>
                      {interval === "yearly" && <span className="text-muted-foreground line-through mr-2">${p.monthly}</span>}
                      <span className="font-serif-display text-5xl">${interval === "yearly" ? p.yearly : p.monthly}</span>
                      <span className="text-muted-foreground"> / month</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mb-6 h-4">
                  {prepaid ? `Paid once for ${interval === "yearly" ? "12 months" : "1 month"} · no auto-renewal`
                    : interval === "yearly" ? `Billed $${(p.yearly * 12).toFixed(2)} yearly` : "Billed monthly"}
                </div>
                <Button data-testid={`pricing-cta-${p.key}`} disabled={!!busy || current} onClick={() => choose(p)}
                  className={`w-full rounded-sm mb-8 btn-invert ${p.highlight ? "bg-primary hover:bg-foreground" : "bg-foreground hover:bg-primary"} text-background`}>
                  {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? "Current plan" : paypal ? "Buy with PayPal →" : paid ? (inr ? "Buy this plan →" : "Switch to this plan →") : "Upgrade now →"}
                </Button>
                <ul className="space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="font-mono-label text-muted-foreground mt-6">{inr ? "Secure checkout by Razorpay · UPI, cards & netbanking · 7-day refund" : paypal ? "Secure checkout by PayPal · PayPal balance, cards & bank · 7-day refund" : "Secure checkout by Stripe · Cancel anytime · 7-day refund"}</p>
      </div>
    </section>
  );
}
